import { ArrowLeftIcon, ArrowTopRightIcon, Pane, Tab, TabNavigation } from "evergreen-ui"
import { matchPath, useHistory } from "react-router-dom";
import React, { useEffect } from "react";
import { ColorPalette } from "../../context/Theme"
import { TabInfo } from "./TabInfo";
import { removeJcContentContainerStylesEffect } from "../removeJcContentContainerStylesEffect";

import './styles/MultiTabView.css';

export interface MultiTabViewProps {
    header?: JSX.Element | string;
    tabs: TabInfo[];
    selectedTabIndex: number;
    onTabSelected: (index: number) => void;
    fullPathFormat?: string;
    backButtonHeader?: string;
    backButtonOnClick?: () => void;
    /** When true, skips URL routing, layout effects, and header/back button rendering. */
    embedded?: boolean;
}

export const MultiTabView = <Params extends { tabId?: string }>({
    header,
    tabs,
    selectedTabIndex,
    onTabSelected,
    fullPathFormat,
    backButtonHeader,
    backButtonOnClick,
    embedded,
}: MultiTabViewProps) => {
    const history = useHistory();

    useEffect(() => {
        if (embedded) return;
        return removeJcContentContainerStylesEffect(false);
    }, [embedded]);

    useEffect(() => {
        if (embedded || !fullPathFormat) {
            return;
        }

        const unsubscribe = history.listen((location) => {
            const result = matchPath<Params>(location.pathname, {
                path: fullPathFormat,
            });
            const index = tabs.findIndex((tab: TabInfo) => {
                return (tab.tabId === result?.params?.tabId);
            });
            onTabSelected((index !== -1) ? index : 0);
        });

        return () => {
            unsubscribe();
        };
    }, [fullPathFormat, embedded]);

    const renderHeader = (): JSX.Element | null => {
        if (!header) return null;
        if (typeof header === "string") {
            return (
                <h3 className={"multi-tab-heading"}>
                    {header}
                </h3>
            );
        }
        return header;
    };

    const renderBackButton = (): JSX.Element | null => {
        if (backButtonHeader) {
            return (
                <div onClick={backButtonOnClick} className={"multi-tab-back-button"}>
                    <ArrowLeftIcon size={13} color={ColorPalette.lightGreyText}></ArrowLeftIcon>
                    {backButtonHeader}
                </div>
            );
        }
        return null;
    };

    return (
        <Pane className={"multi-tab-container"}>
            <Pane className={"multi-tab-nav-container"}>
                {renderBackButton()}
                {renderHeader()}
                <TabNavigation className={"multi-tab-nav"}>
                    {tabs.map((tab: TabInfo, index: number) => (
                        <Tab
                            appearance={tab.openInNewTab ? "secondary" : "primary"}
                            color={tab.locked ?
                                ColorPalette.disabledText :
                                ColorPalette.primaryText
                            }
                            key={tab.label}
                            id={tab.label}
                            style={{
                                fontSize: "14px",
                                pointerEvents: tab.locked ? "none" : "auto"
                            }}
                            onSelect={() => {
                                if (tab.redirect) {
                                    if (tab.openInNewTab) {
                                        window.open(tab.redirect, "_blank");
                                        return;
                                    }

                                    if (tab.useHref) {
                                        window.location.href = window.location.origin + tab.redirect;
                                        return;
                                    }

                                    if (!embedded) {
                                        history.push(tab.redirect);
                                    }
                                } else {
                                    onTabSelected(index);
                                }
                            }}
                            isSelected={index === selectedTabIndex}
                            aria-controls={`panel-${tab.label}`}
                            paddingX={0}
                            flexShrink={0}
                        >
                            {tab.label}
                            {tab.icon ? <span style={{marginLeft: 4, display: "inline-flex", verticalAlign: "middle"}}>{tab.icon}</span> : null}
                            {" "}
                            {tab.openInNewTab ? (
                                <ArrowTopRightIcon
                                    size={13}
                                    marginLeft={5}
                                    color={
                                        tab.locked
                                            ? ColorPalette.disabledText
                                            : ColorPalette.primaryText
                                    }
                                />
                            ) : null}
                        </Tab>
                    ))}
                </TabNavigation>
            </Pane>
            <Pane className={`multi-tab-content-container ${tabs[selectedTabIndex]?.useDefaultLayout ? "container-default-layout" : ""}`}>
                {tabs[selectedTabIndex]?.component}
            </Pane>
        </Pane>
    );
}
